"use client";

import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { useCalculationCycleCurrent } from "@/features/tuition/hooks/useCalculationCycleCurrent";
import {
  useActiveTuitionCalculationCycleId,
  useTuitionWorkflowSession,
} from "@/features/tuition/hooks/useTuitionWorkflowSession";
import { shouldFetchCurrentCalculationCycle } from "@/features/tuition/utils/tuition-workflow-session";
import { CalculationCycleStatus } from "@/openapi/generated";

export function useTuitionCalculationActivity() {
  const pathname = usePathname();
  const isAdminDashboard = pathname?.startsWith("/admin") ?? false;
  const activeCycleId = useActiveTuitionCalculationCycleId();
  const shouldTrack = shouldFetchCurrentCalculationCycle(
    isAdminDashboard,
    activeCycleId,
  );

  const { data: cycle } = useCalculationCycleCurrent({
    enabled: shouldTrack,
    poll: shouldTrack,
  });

  const session = useTuitionWorkflowSession(shouldTrack ? activeCycleId : null);

  const isRunning = useMemo(() => {
    if (!shouldTrack || !session?.calculationStarted) return false;

    const isTerminalStatus =
      cycle?.status === CalculationCycleStatus.Calculated ||
      cycle?.status === CalculationCycleStatus.CalculationFailed;

    return !isTerminalStatus;
  }, [cycle?.status, session?.calculationStarted, shouldTrack]);

  return {
    isRunning,
    studentCount: session?.studentCount ?? 0,
    cycle,
  };
}
